import { isProduction } from '../../../constants/environment';
import { type Env, isLive } from '../../../context/env-context';
import bigCommerceCore from '../assets/snippets/bigcommerce/core';
import { CSS_SNIPPETS } from '../assets/snippets/css';
import customCore from '../assets/snippets/custom/core';
import shopifyCore from '../assets/snippets/shopify/core';
import { type SnippetName, type SnippetPlatform, tagSnippet } from '../assets/snippets/tags';
import woocommerceCore from '../assets/snippets/woocommerce/core';
import { SPARKJS_LEGACY_FALLBACK_VERSION } from '../constants';
import { isShopify } from './platform';

/** Anything without a bespoke template gets the generic `custom` markup. */
function snippetPlatform(platform: Env['platform']): SnippetPlatform {
  if (isShopify(platform)) return 'shopify';
  if (platform === 'woocommerce') return 'woocommerce';
  return 'custom';
}

function sparkJsUrl(env: Env): string {
  if (!env.sparkjs_version) {
    return `https://cdn.sparklayer.io/spark.${SPARKJS_LEGACY_FALLBACK_VERSION}.js`;
  }

  const prefix = isProduction ? '' : 'dev.';
  return `https://${prefix}sparkcdn.io/sparkjs/${env.team.slugged_name}/${isLive(env.environment) ? 'live' : 'test'}`;
}

function domainCode(env: Env): string {
  const live = isLive(env.environment);
  if (live && isProduction) return '';

  const domain = live ? window.location.hostname : `test.${window.location.hostname}`;
  return `\n  sparkDomain: "${domain}",`;
}

function coreTemplate(platform: Env['platform']): string {
  if (isShopify(platform)) return shopifyCore;
  if (platform === 'bigcommerce') return bigCommerceCore;
  if (platform === 'woocommerce') return woocommerceCore;
  return customCore;
}

export function buildCoreSnippet(env: Env): string {
  const code = coreTemplate(env.platform)
    .replace('#SPARKJS_URL#', sparkJsUrl(env))
    .replace('#SITE_ID#', env.team.slugged_name)
    .replace('#DOMAIN#', domainCode(env));

  return `\n\n${code}\n`;
}

/** Platforms with their own guard or palette; everything else takes the `base` sample. */
const CSS_BY_PLATFORM: Partial<Record<NonNullable<Env['platform']>, string>> = {
  bigcommerce: CSS_SNIPPETS.bigcommerce,
  wix: CSS_SNIPPETS.wix,
  woocommerce: CSS_SNIPPETS.woocommerce,
};

export function buildCssSnippet(env: Env): string {
  const code = (env.platform && CSS_BY_PLATFORM[env.platform]) || CSS_SNIPPETS.base;
  return `\n\n${code}\n`;
}

export function buildTagSnippet(platform: Env['platform'], name: SnippetName): string {
  return `\n\n${tagSnippet(snippetPlatform(platform), name)}\n`;
}
